import { useSharedWithMeCanvases } from "@/api/canvas";
import { Spinner } from "@cohesive/ui-components";
import { Users } from "lucide-react";
import { useEffect, useMemo } from "react";
import { WorkspacePageHeader } from "./components/WorkspacePageHeader";
import { WorkspaceProviders } from "./components/WorkspaceProviders";
import { WorkspaceShell } from "./components/WorkspaceShell";
import { useCanvasCardActions } from "./hooks/useCanvasCardActions";
import { useWorkspaceSelectionOptional } from "./hooks/useWorkspaceSelection";
import { workspaceSharedCanvasGridClassName } from "./workspaceGridClasses";

export function SharedWithMePage() {
  return (
    <WorkspaceProviders>
      <SharedWithMePageInner />
    </WorkspaceProviders>
  );
}

function SharedWithMePageInner() {
  const { data: canvases, isLoading } = useSharedWithMeCanvases();
  const selection = useWorkspaceSelectionOptional();

  const sortedCanvases = useMemo(
    () =>
      [...(canvases ?? [])].sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      ),
    [canvases],
  );

  const orderedIds = useMemo(
    () => sortedCanvases.map((c) => `canvas:${c.id}`),
    [sortedCanvases],
  );

  useEffect(() => {
    selection?.setOrderedIds(orderedIds);
  }, [orderedIds, selection]);

  const { renderCanvasCard, dialogs } = useCanvasCardActions({
    canvases: sortedCanvases,
  });

  return (
    <>
      <title>Shared with me</title>
      <WorkspaceShell isLoading={isLoading}>
        <WorkspacePageHeader
          breadcrumbs={[
            { label: "Home", to: "/" },
            { label: "Shared with me" },
          ]}
        />

        <div
          className="flex-1 overflow-y-auto overflow-x-hidden"
          onClick={selection?.clearIfOutside}
        >
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <Spinner size="lg" />
            </div>
          ) : sortedCanvases.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-3 text-center">
              <Users className="h-10 w-10 text-muted-foreground/20" />
              <p className="text-sm text-muted-foreground">
                Nothing shared with you yet.
              </p>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 md:px-8 lg:px-10 pt-6 pb-12">
              {/* Fixed-width tiles (matches WorkspaceDetailPage and
                  WorkspaceIndexPage) so canvas cards read identically
                  across surfaces — auto-fill keeps the tile size
                  predictable as the viewport grows or shrinks. */}
              <div className={workspaceSharedCanvasGridClassName}>
                {sortedCanvases.map((canvas, index) =>
                  renderCanvasCard(canvas, index),
                )}
              </div>
            </div>
          )}
        </div>
      </WorkspaceShell>

      {dialogs}
    </>
  );
}

export default SharedWithMePage;
